import React, { useId } from "react";
import { HolidayKey } from "../types";
import { Shaded, ShadeColors } from "../scenes/toon";
import { SantaBust } from "../scenes/Santa";
import { HeresJohnnyIcon, ICON_PAD } from "./HeresJohnnyIcon";
import { PumpkinIcon } from "./PumpkinIcon";

/**
 * Ikonerna som sitter i mitten av Fikahjulet under respektive högtid.
 * Alla ritas i en 100x100-viewBox så att de kan bytas ut mot varandra.
 * De återanvänds även som dekorationer i bakgrundsscenerna.
 */
type IconProps = React.SVGProps<SVGSVGElement>;

/* ------------------------------------------------------------------ */
/* Påsk: dekorerat påskägg                                             */
/* ------------------------------------------------------------------ */
export const EasterEggIcon: React.FC<IconProps> = props => {
  const id = useId().replace(/:/g, "");
  const egg = "M50 5 C 29 5, 17 36, 17 58 C 17 80, 32 96, 50 96 C 68 96, 83 80, 83 58 C 83 36, 71 5, 50 5 Z";
  return (
    <svg viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg" {...props}>
      <defs>
        <clipPath id={`egg-${id}`}>
          <path d={egg} />
        </clipPath>
        <linearGradient id={`eggg-${id}`} x1="0" y1="0" x2="0.4" y2="1">
          <stop offset="0" stopColor="#fde68a" />
          <stop offset="1" stopColor="#fbbf24" />
        </linearGradient>
      </defs>
      <path d={egg} fill={`url(#eggg-${id})`} />
      <g clipPath={`url(#egg-${id})`}>
        {/* Sicksackband */}
        <polyline
          points="8,56 18,46 28,56 38,46 48,56 58,46 68,56 78,46 88,56 98,46"
          fill="none"
          stroke="#fffbeb"
          strokeWidth="5"
          strokeLinejoin="round"
          strokeLinecap="round"
        />
        {/* Ränder */}
        <rect x="0" y="66" width="100" height="3.5" fill="#d97706" />
        <rect x="0" y="73" width="100" height="3.5" fill="#fffbeb" />
        <rect x="0" y="80" width="100" height="3.5" fill="#d97706" />
        {/* Prickar */}
        {[
          [35, 30], [50, 23], [65, 30], [42, 38], [58, 38], [50, 88],
        ].map(([x, y], i) => (
          <circle key={i} cx={x} cy={y} r="3.2" fill={i % 2 ? "#fffbeb" : "#b45309"} />
        ))}
      </g>
      <ellipse cx="36" cy="27" rx="6" ry="12" transform="rotate(-18 36 27)" fill="#ffffff" opacity="0.45" />
    </svg>
  );
};

/* ------------------------------------------------------------------ */
/* Midsommar: krans med blommor och blågula band                        */
/* ------------------------------------------------------------------ */
export const MidsummerWreathIcon: React.FC<IconProps> = props => {
  const leaves = Array.from({ length: 26 }, (_, i) => (i / 26) * 360);
  const greens = ["#22c55e", "#16a34a", "#15803d", "#4ade80"];
  const flowerAngles = [0, 45, 90, 135, 180, 225, 270, 315];
  const r = 33;
  return (
    <svg viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg" {...props}>
      {/* Band i svenska färger */}
      <path d="M45 82 C 38 90, 44 96, 38 100" fill="none" stroke="#2563eb" strokeWidth="3.5" strokeLinecap="round" />
      <path d="M55 82 C 62 90, 56 96, 62 100" fill="none" stroke="#facc15" strokeWidth="3.5" strokeLinecap="round" />
      <circle cx="50" cy="50" r={r} fill="none" stroke="#166534" strokeWidth="11" />
      {leaves.map((a, i) => {
        const rad = (a * Math.PI) / 180;
        const x = 50 + Math.cos(rad) * r;
        const y = 50 + Math.sin(rad) * r;
        const tilt = i % 2 ? 35 : -35;
        return (
          <ellipse
            key={i}
            cx={x}
            cy={y}
            rx="4.2"
            ry="8"
            fill={greens[i % greens.length]}
            transform={`rotate(${a + tilt} ${x} ${y})`}
          />
        );
      })}
      {flowerAngles.map((a, i) => {
        const rad = (a * Math.PI) / 180;
        const x = 50 + Math.cos(rad) * r;
        const y = 50 + Math.sin(rad) * r;
        const kind = i % 3;
        if (kind === 0) {
          // Prästkrage
          return (
            <g key={i}>
              {Array.from({ length: 7 }, (_, k) => {
                const pa = (k / 7) * Math.PI * 2;
                return (
                  <ellipse
                    key={k}
                    cx={x + Math.cos(pa) * 4}
                    cy={y + Math.sin(pa) * 4}
                    rx="2.2"
                    ry="3.4"
                    fill="#ffffff"
                    transform={`rotate(${(pa * 180) / Math.PI + 90} ${x + Math.cos(pa) * 4} ${y + Math.sin(pa) * 4})`}
                  />
                );
              })}
              <circle cx={x} cy={y} r="2.8" fill="#facc15" />
            </g>
          );
        }
        if (kind === 1) {
          // Blåklint
          return (
            <g key={i}>
              {Array.from({ length: 8 }, (_, k) => {
                const pa = (k / 8) * Math.PI * 2;
                return (
                  <path
                    key={k}
                    d="M0 0 L-2.2 -5.5 L0 -4.2 L2.2 -5.5 Z"
                    fill="#3b82f6"
                    transform={`translate(${x} ${y}) rotate(${(pa * 180) / Math.PI})`}
                  />
                );
              })}
              <circle cx={x} cy={y} r="2" fill="#1e3a8a" />
            </g>
          );
        }
        // Rosa klöver/ängsblomma
        return (
          <g key={i}>
            {Array.from({ length: 5 }, (_, k) => {
              const pa = (k / 5) * Math.PI * 2;
              return (
                <circle
                  key={k}
                  cx={x + Math.cos(pa) * 3.2}
                  cy={y + Math.sin(pa) * 3.2}
                  r="2.6"
                  fill="#f472b6"
                />
              );
            })}
            <circle cx={x} cy={y} r="1.8" fill="#fdf2f8" />
          </g>
        );
      })}
    </svg>
  );
};

/* ------------------------------------------------------------------ */
/* Jul: jultomten i klassisk målad reklamstil (se scenes/Santa.tsx)     */
/* ------------------------------------------------------------------ */
export const TomteIcon: React.FC<IconProps> = props => (
  <svg viewBox="0 0 400 400" xmlns="http://www.w3.org/2000/svg" {...props}>
    <SantaBust />
  </svg>
);

/* ------------------------------------------------------------------ */
/* Lucia: luciakrona med lingonris och levande ljus                     */
/* ------------------------------------------------------------------ */
const WAX: ShadeColors = { base: "#ffffff", shade: "#d7dcef", light: "#ffffff", rim: "#eceffa" };
const LEAF_C: ShadeColors = { base: "#3f8a4a", shade: "#1f5230", light: "#7cc27f" };

export const LuciaCrownIcon: React.FC<IconProps> = props => {
  const id = useId().replace(/:/g, "");
  const candles = [
    { x: 20, top: 44, h: 22 },
    { x: 35, top: 34, h: 30 },
    { x: 50, top: 28, h: 36 },
    { x: 65, top: 34, h: 30 },
    { x: 80, top: 44, h: 22 },
  ];
  const leaves = Array.from({ length: 30 }, (_, i) => i);
  return (
    <svg viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg" {...props}>
      <defs>
        <radialGradient id={`fl-${id}`} cx="50%" cy="70%" r="60%">
          <stop offset="0%" stopColor="#ffffff" />
          <stop offset="40%" stopColor="#fff1a8" />
          <stop offset="100%" stopColor="#ff9f2e" />
        </radialGradient>
        <radialGradient id={`gl-${id}`} cx="50%" cy="50%" r="50%">
          <stop offset="0%" stopColor="#ffe7a3" stopOpacity="0.85" />
          <stop offset="100%" stopColor="#ffb44d" stopOpacity="0" />
        </radialGradient>
      </defs>
      {/* Bakre delen av kransen */}
      <ellipse cx="50" cy="68" rx="38" ry="11" fill="#1d4a2b" />
      {candles.map((c, i) => (
        <g key={i}>
          <circle cx={c.x} cy={c.top - 8} r="13" fill={`url(#gl-${id})`} className="hol-flicker" style={{ animationDelay: `${-i * 0.7}s` }} />
          <Shaded d={`M${c.x - 4.2} ${c.top} L ${c.x - 4.2} ${c.top + c.h} L ${c.x + 4.2} ${c.top + c.h} L ${c.x + 4.2} ${c.top} C ${c.x + 2} ${c.top + 2}, ${c.x - 2} ${c.top + 2}, ${c.x - 4.2} ${c.top} Z`} c={WAX} depth={2.4} />
          <path d={`M${c.x + 1.5} ${c.top + 1} q 1.6 4 0 7`} stroke="#ffffff" strokeWidth="1.6" fill="none" strokeLinecap="round" opacity="0.9" />
          <path d={`M${c.x} ${c.top} V ${c.top - 2.5}`} stroke="#3a2a20" strokeWidth="1" />
          <path
            d={`M${c.x} ${c.top - 13} C ${c.x + 4} ${c.top - 8}, ${c.x + 3.5} ${c.top - 2.5}, ${c.x} ${c.top - 2} C ${c.x - 3.5} ${c.top - 2.5}, ${c.x - 4} ${c.top - 8}, ${c.x} ${c.top - 13} Z`}
            fill={`url(#fl-${id})`}
            className="hol-flicker"
            style={{ animationDelay: `${-i * 0.7}s` }}
          />
        </g>
      ))}
      {/* Lingonris runt kronan */}
      {leaves.map(i => {
        const a = (i / leaves.length) * Math.PI * 2;
        const x = 50 + Math.cos(a) * 36;
        const y = 70 + Math.sin(a) * 10;
        const front = Math.sin(a) > -0.2;
        return (
          <ellipse
            key={i}
            cx={x}
            cy={y}
            rx="6"
            ry="3.4"
            transform={`rotate(${(a * 180) / Math.PI + 90 + (i % 2 ? 30 : -30)} ${x} ${y})`}
            fill={front ? (i % 3 === 0 ? LEAF_C.light : LEAF_C.base) : LEAF_C.shade}
          />
        );
      })}
      {leaves
        .filter(i => i % 3 === 1)
        .map(i => {
          const a = (i / leaves.length) * Math.PI * 2 + 0.1;
          const x = 50 + Math.cos(a) * 37;
          const y = 71 + Math.sin(a) * 10;
          return Math.sin(a) > -0.2 ? (
            <g key={`b${i}`}>
              <circle cx={x} cy={y} r="2.8" fill="#d9243a" />
              <circle cx={x - 0.9} cy={y - 0.9} r="0.9" fill="#ffffff" opacity="0.8" />
            </g>
          ) : null;
        })}
    </svg>
  );
};

export const WHEEL_ICONS: Record<HolidayKey, React.FC<IconProps>> = {
  easter: EasterEggIcon,
  midsummer: MidsummerWreathIcon,
  halloween: PumpkinIcon,
  lucia: LuciaCrownIcon,
  christmas: TomteIcon,
};

/**
 * Ikoner som visas i hjulets mitt först när hjulet har snurrat klart.
 * Halloween: pumpan byts mot "Jag är också bjuden!"-memen när hjulet stannat.
 */
export const WHEEL_REVEAL_ICONS: Partial<Record<HolidayKey, React.FC<IconProps>>> = {
  halloween: HeresJohnnyIcon,
};

/** Den avslöjade ikonens storlek relativt standardstorleken (fyller mittcirkeln). */
export const WHEEL_REVEAL_SCALE: Partial<Record<HolidayKey, number>> = {
  // Ikonen har en tom marginal runt rutan (ICON_PAD), så den förstoras lika mycket.
  halloween: 1.78 * ((200 + 2 * ICON_PAD) / 200),
};
